import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { DocumentApi } from "~/functions/api/document";
import { ProcessLockApi } from "~/functions/api/processLock";
import type { RootState } from "~/store";

interface RowData {
  機能名: string;
  適用アプリ名: string;
  概要: string;
  入力フィールド: string;
  出力フィールド: string;
  処理条件: string;
  機能詳細: string;
  設定情報: string;
  イベント契機: string;
  対応端末: string;
  対応区分: string;
}

interface DocumentInfo {
  createdAt: string;
  modifiedAt: string;
  modifiedBy?: {
    display_name: string;
  };
}

interface CustomizeSpecificationListState {
  data: RowData[];
  copyData: RowData[];
  isEditable: boolean;
  isLoading: boolean;
  error: string | null;
  documentInfo: DocumentInfo | null;
  sources: { type: string; id: string }[];
}

const initialState: CustomizeSpecificationListState = {
  data: [],
  copyData: [],
  isEditable: false,
  isLoading: true,
  error: null,
  documentInfo: null,
  sources: [],
};

// APIインスタンスの作成
const documentApi = new DocumentApi();
const processLockApi = new ProcessLockApi();

// カスタマイズ仕様書データの取得
export const fetchCustomizeSpecificationList = createAsyncThunk(
  "customizeSpecificationList/fetch",
  async (documentId: string | undefined, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;

      // URLパラメータから取得したIDを優先、なければReduxストアから取得
      const docId = documentId || currentProject.outputs?.["customize-specification"]?.id;
      if (!docId) {
        return rejectWithValue("カスタマイズ仕様書が見つかりません");
      }

      const response = await documentApi.get(currentUser.organization_id, currentProject.id, docId);

      if (!response?.content) {
        return rejectWithValue("ドキュメントの取得に失敗しました。");
      }

      const parsedContent = JSON.parse(response.content);
      const rows: string[][] = parsedContent.rows || [];

      return {
        data: rows.map((row) => ({
          機能名: row[0] || "",
          適用アプリ名: row[1] || "",
          概要: row[2] || "",
          入力フィールド: row[3] || "",
          出力フィールド: row[4] || "",
          処理条件: row[5] || "",
          機能詳細: row[6] || "",
          設定情報: row[7] || "",
          イベント契機: row[8] || "",
          対応端末: row[9] || "",
          対応区分: row[10] || "",
        })),
        documentInfo: {
          createdAt: response.created_at || "",
          modifiedAt: response.modified_at || "",
          modifiedBy: response.modified_by,
        },
        sources: response.sources ?? [],
      };
    } catch (_error) {
      return rejectWithValue("詳細データの取得中にエラーが発生しました。");
    }
  },
);

// ロックの取得
export const acquireLock = createAsyncThunk(
  "customizeSpecificationList/acquireLock",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;

      if (!currentProject.outputs?.["customize-specification"]?.id) {
        return rejectWithValue("カスタマイズ仕様書が見つかりません");
      }

      const response = await processLockApi.lock(currentUser.organization_id, currentProject.id, {
        type: "customize-specification",
        mode: "edit",
      });

      if (response === null) {
        return rejectWithValue("編集権限を取得できませんでした");
      }

      return true;
    } catch (_error) {
      return rejectWithValue("ロックの取得中にエラーが発生しました");
    }
  },
);

// ロックの解除
export const releaseLock = createAsyncThunk(
  "customizeSpecificationList/releaseLock",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;

      if (!currentProject.outputs?.["customize-specification"]?.id) {
        return rejectWithValue("カスタマイズ仕様書が見つかりません");
      }

      await processLockApi.unlock(currentUser.organization_id, currentProject.id, {
        type: "customize-specification",
        mode: "edit",
      });

      return true;
    } catch (_error) {
      return rejectWithValue("ロックの解除中にエラーが発生しました");
    }
  },
);

// データの更新
export const updateCustomizeSpecificationList = createAsyncThunk(
  "customizeSpecificationList/update",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;
      const { data } = state.customizeSpecificationList;

      if (!currentProject.outputs?.["customize-specification"]?.id) {
        return rejectWithValue("カスタマイズ仕様書が見つかりません");
      }

      const headers = [
        "機能名",
        "適用アプリ名",
        "概要",
        "入力フィールド",
        "出力フィールド",
        "処理条件",
        "機能詳細",
        "設定情報",
        "イベント契機",
        "対応端末",
        "対応区分",
      ];
      const rows = data.map((row) => headers.map((header) => row[header as keyof RowData] || ""));

      const updateData = {
        content: JSON.stringify({
          headers,
          rows,
        }),
      };

      // ドキュメントのアップデートAPIリクエスト
      const response = await documentApi.updateContent(
        currentUser?.organization_id,
        currentProject.id,
        currentProject.outputs?.["customize-specification"]?.id,
        updateData,
      );

      if (response === null) {
        return rejectWithValue("ドキュメントの更新に失敗しました");
      }

      // データの更新が成功したら、ロックを解除
      await dispatch(releaseLock());
      // ロック解除後にカスタマイズ仕様を再取得
      await dispatch(fetchCustomizeSpecificationList());

      return true;
    } catch (error) {
      return rejectWithValue(`保存中にエラーが発生しました: ${(error as Error)?.message}`);
    }
  },
);

// ドキュメント情報の取得は削除（fetchCustomizeSpecificationListで統合済み）

const customizeSpecificationListSlice = createSlice({
  name: "customizeSpecificationList",
  initialState,
  reducers: {
    setData: (state, action: PayloadAction<RowData[]>) => {
      state.data = action.payload;
    },
    setCopyData: (state, action: PayloadAction<RowData[]>) => {
      state.copyData = action.payload;
    },
    restoreFromCopyData: (state) => {
      state.data = JSON.parse(JSON.stringify(state.copyData));
    },
    updateField: (
      state,
      action: PayloadAction<{ rowIndex: number; columnId: keyof RowData; value: string }>,
    ) => {
      const { rowIndex, columnId, value } = action.payload;
      const row = state.data[rowIndex];
      if (row) {
        row[columnId] = value;
      }
    },
    addRow: (state) => {
      const newRow: RowData = {
        機能名: "",
        適用アプリ名: "",
        概要: "",
        入力フィールド: "",
        出力フィールド: "",
        処理条件: "",
        機能詳細: "",
        設定情報: "",
        イベント契機: "",
        対応端末: "",
        対応区分: "",
      };
      state.data.push(newRow);
    },
    deleteRow: (state, action: PayloadAction<number>) => {
      state.data = state.data.filter((_, index) => index !== action.payload);
    },
    clearError: (state) => {
      state.error = null;
    },
    updateModifiedAt: (state) => {
      if (state.documentInfo) {
        state.documentInfo.modifiedAt = new Date().toISOString();
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchCustomizeSpecificationList
      .addCase(fetchCustomizeSpecificationList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCustomizeSpecificationList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.sources = action.payload.sources;
        if (action.payload.documentInfo.modifiedBy) {
          state.documentInfo = {
            createdAt: action.payload.documentInfo.createdAt,
            modifiedAt: action.payload.documentInfo.modifiedAt,
            modifiedBy: {
              display_name: action.payload.documentInfo.modifiedBy.display_name,
            },
          };
        } else {
          state.documentInfo = {
            createdAt: action.payload.documentInfo.createdAt,
            modifiedAt: action.payload.documentInfo.modifiedAt,
          };
        }
        state.error = null;
      })
      .addCase(fetchCustomizeSpecificationList.rejected, (state, action) => {
        state.isLoading = false;
        state.data = [];
        state.error = action.payload as string;
      })
      // acquireLock
      .addCase(acquireLock.fulfilled, (state) => {
        state.isEditable = true;
        state.copyData = JSON.parse(JSON.stringify(state.data)); // 編集開始時にデータをコピー
      })
      .addCase(acquireLock.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // releaseLock
      .addCase(releaseLock.fulfilled, (state) => {
        state.isEditable = false;
      })
      .addCase(releaseLock.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // updateCustomizeSpecificationList
      .addCase(updateCustomizeSpecificationList.fulfilled, (state) => {
        state.isEditable = false;
      })
      .addCase(updateCustomizeSpecificationList.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  setData,
  setCopyData,
  restoreFromCopyData,
  updateField,
  addRow,
  deleteRow,
  clearError,
  updateModifiedAt,
} = customizeSpecificationListSlice.actions;

export default customizeSpecificationListSlice.reducer;
